import api from "@/utils/axios";

const ATTRIBUTE_API_URL = `/attributes`;

export default {
  namespaced: true,
  state: {
    attribute: null,
    attributes: [],
    meta: {},
    loading: false,
    error: null,
  },
  mutations: {
    SET_ATTRIBUTE(state, attribute) {
      state.attribute = attribute;
    },
    SET_ATTRIBUTES(state, attributes) {
      state.attributes = attributes;
    },
    SET_META(state, meta) {
      state.meta = meta;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ATTRIBUTE(state) {
      state.attribute = null;
      state.error = null;
    },
    CLEAR_ATTRIBUTES(state) {
      state.attributes = [];
      state.meta = {};
      state.error = null;
    },
  },
  actions: {
    async fetchAttribute({ commit }, attributeId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${ATTRIBUTE_API_URL}/${attributeId}/`);
        commit("SET_ATTRIBUTE", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch attribute"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async selectAttributes({ commit }, searchTerm = "") {
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${ATTRIBUTE_API_URL}/select/`, {
          params: { q: searchTerm },
        });
        return res.data.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch attributes"
        );
        return [];
      }
    },

    async filterAttributes({ commit }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${ATTRIBUTE_API_URL}/filter/`, payload);
        commit("SET_ATTRIBUTES", res.data.data);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch attributes"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async lazyLoadAttributes({ commit, state }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${ATTRIBUTE_API_URL}/filter/`, payload);
        const newAttributes = res.data.data;
        const updatedList = [...state.attributes, ...newAttributes];
        commit("SET_ATTRIBUTES", updatedList);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to load more attributes"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewAttribute({ commit }, data) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${ATTRIBUTE_API_URL}/new/`, data, {
          successNotify: false,
          errorNotify: true,
        });
        commit("SET_ATTRIBUTE", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to create attribute"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateAttribute({ commit }, { attributeId, data }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(
          `${ATTRIBUTE_API_URL}/${attributeId}/`,
          data,
          {
            successNotify: false,
            errorNotify: true,
          }
        );
        commit("SET_ATTRIBUTE", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to update attribute"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteAttribute({ commit }, attributeId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        await api.delete(`${ATTRIBUTE_API_URL}/${attributeId}/`);
        commit("CLEAR_ATTRIBUTE");
        return true;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to delete attribute"
        );
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    clearAttribute({ commit }) {
      commit("CLEAR_ATTRIBUTE");
    },

    clearAttributes({ commit }) {
      commit("CLEAR_ATTRIBUTES");
    },
  },
  getters: {
    attribute: (state) => state.attribute,
    attributes: (state) => state.attributes,
    attributeMeta: (state) => state.meta,
    attributeLoading: (state) => state.loading,
    attributeError: (state) => state.error,
  },
};
